import { useForm } from "react-hook-form";

import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@alaffia-technology-solutions/ds";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const pdfAnnotationFormSchema = z.object({
  comment: z.string().trim().min(1).nullable(),
  labels: z.array(z.string().min(1)),
});

export type PDFAnnotationFormData = z.infer<typeof pdfAnnotationFormSchema>;

interface AnnotationLabel {
  label: string;
  title: string;
}

interface PDFAnnotationFormProps {
  onSubmit: (data: PDFAnnotationFormData) => void;
  annotationLabels?: AnnotationLabel[];
}

export function PDFAnnotationForm({
  onSubmit,
  annotationLabels,
}: PDFAnnotationFormProps) {
  const form = useForm<PDFAnnotationFormData>({
    resolver: zodResolver(pdfAnnotationFormSchema),
    defaultValues: {
      comment: null,
      labels: [],
    },
  });
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="af-flex af-flex-col af-gap-2 af-p-2"
      >
        <FormField
          control={form.control}
          name="comment"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Create user annotation</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Add your comment"
                  {...field}
                  onChange={(e) => field.onChange(e.target.value || null)}
                  value={field.value ?? ""}
                  className="af-w-60 af-p-2"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {annotationLabels && annotationLabels.length > 0 && (
          <FormField
            control={form.control}
            name="labels"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Labels</FormLabel>
                <Select
                  defaultValue={field.value[0]}
                  onValueChange={(v) => field.onChange([v])}
                >
                  <FormControl>
                    <SelectTrigger className="af-bg-background">
                      <SelectValue placeholder="Select label" />
                    </SelectTrigger>
                  </FormControl>
                  <FormMessage />
                  <SelectContent>
                    {annotationLabels.map((annotation) => (
                      <SelectItem
                        key={annotation.label}
                        value={annotation.label}
                      >
                        {annotation.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        )}
        <Button type="submit" size="sm">
          Save
        </Button>
      </form>
    </Form>
  );
}
